/**
 * Worker <-> main contract: JSON control messages plus a binary snapshot
 * stream over transferable ArrayBuffers (3-buffer ping-pong pool).
 */
import { PARAMS } from './params';
import type { AwayReport, SaveV1 } from '../save/schema';

// ---- control messages ----

export type VerbMsg =
  | { t: 'verb'; kind: 'tapEgg'; x: number; z: number }
  | { t: 'verb'; kind: 'apple'; x: number; z: number }
  | { t: 'verb'; kind: 'ballPlace'; x: number; z: number }
  | { t: 'verb'; kind: 'ballThrow'; vx: number; vz: number; power: number }
  | { t: 'verb'; kind: 'pet'; id: number }
  | { t: 'verb'; kind: 'stroke'; id: number } // held rub — streamed ~4/s while rubbing
  | { t: 'verb'; kind: 'beacon'; x: number; z: number }
  | { t: 'verb'; kind: 'song'; x: number; z: number }; // double-tap: start a dance

export type MainToWorker =
  | { t: 'init'; seed: string; count: number; save?: SaveV1 | null; now?: number; startPaused?: boolean }
  | { t: 'requestSave'; now: number }
  | VerbMsg
  | { t: 'scrub'; points: { x: number; z: number }[] }
  | { t: 'nudge'; kind: 'hunger' | 'grime' | 'ball' } // onboarding pacing pokes
  | { t: 'answer'; qid: string; choice: 0 | 1 } // fourth-wall question answered
  | { t: 'inspect'; id: number } // long-press a creature -> full details
  | { t: 'setPop'; count: number } // debug: force population
  | { t: 'warp'; mult: number } // debug: time warp (1..200 sim ticks per real tick)
  | { t: 'skip'; hours: number } // debug: jump time via the offline macro sim
  | { t: 'pause' }
  | { t: 'resume' }
  | { t: 'bufback'; buf: ArrayBuffer };

export interface WorkerStats {
  t: 'stats';
  tick: number;
  pop: number;
  tickMs: number; // EMA of sim tick cost
  stage: number; // language ladder 0..4 (monotonic)
  trust: number;
  attachment: number;
  curiosity: number;
  fairness: number;
}

/** Sim events for SFX/particles/UI, batched per snapshot send. */
export const EV = {
  EGG_CRACK: 1,
  HATCH: 2,
  EAT: 3,
  APPLE_LAND: 4,
  PLOP: 5, // grime appeared
  SCRUB_POP: 6, // grime cleaned away
  BOING: 7, // ball bounce/kick
  CHIRP: 8, // creature vocalizes; a = voice seed, b = mood 0..255
  PET: 9, // a = voice seed
  APPLE_GONE: 10,
  MITOSIS: 11, // a = child voice seed, b = parent voice seed
  DORMANT: 12, // a creature curled into a moss ball; a = voice seed
  WAKE: 13, // revived by scrubbing; a = voice seed
  BUILT: 14, // the throng raised a structure; a = item type
  UTTER: 15, // a creature speaks; a = voice seed, b = packUtter(stage, intent)
  DANCE_START: 16, // spontaneous dance genesis (contagion may follow)
  WAKE_PROGRESS: 17, // scrubbing a moss ball; a = voice seed, b = progress 0..255
  BEACON: 18, // the player called; creatures are coming
  SONG: 19, // the player sang; a = how many joined the dance
  SPLASH: 20, // a creature splashing in a pond
} as const;

export type EvKind = (typeof EV)[keyof typeof EV];

/** [kind, x, z, a, b] — a/b are event-specific params. */
export type SimEvent = [EvKind, number, number, number, number];

export interface WorkerEvents {
  t: 'ev';
  l: SimEvent[];
}

export interface WorkerSave {
  t: 'savePayload';
  save: SaveV1;
}

export interface WorkerAway {
  t: 'away';
  report: AwayReport;
}

/** Ground stigmergy for terrain rendering: GRID_N² cells × 2 bytes (wear, joy). */
export interface WorkerGrid {
  t: 'grid';
  buf: ArrayBuffer;
}

/** Everything about one creature, for the inspect card. */
export interface InspectData {
  t: 'inspectData';
  id: number;
  seed: number;
  ageSec: number;
  lifeState: number;
  hunger: number;
  clean: number;
  fun: number;
  rest: number;
  familiarity: number;
  genes: number[];
}

export type WorkerToMain =
  | WorkerStats
  | WorkerEvents
  | WorkerSave
  | WorkerAway
  | WorkerGrid
  | InspectData; // snapshots travel as raw ArrayBuffers

// ---- snapshot binary layout ----
//
// Header (16 B):  u32 tick | f32 simTimeSec | u16 creatureCount | u16 itemCount
//                 | u16 dayPhase (0..65535 over the day) | u16 pad
// Per creature (12 B, little-endian):
//   i16 x, i16 z        fixed-point 1/256 world units (±128 range)
//   u8 heading          0..255 -> 0..2pi (faces +Z at 0)
//   u8 anim             0 idle, 1 hop, 2 eat, 4 sleep, 5 dormant
//   u8 emotion          0 glum .. 255 thriving
//   u8 scaleQ           scale * 200 (0.6 juvenile .. 1.2 adult)
//   u8 seed  u8 phase   stable per-creature variation
//   u8 life  u8 pad     life-state ladder (LIFE enum)
// Per item (12 B):
//   u16 id | u8 type | u8 state | i16 x | i16 z | i16 y | u8 aux | u8 pad

export const SNAP_HEADER_BYTES = 16;
export const SNAP_STRIDE = 12;
export const ITEM_STRIDE = 12;
export const POS_SCALE = 256;
export const SCALE_Q = 200;

export function snapshotBytes(creatureCap: number, itemCap: number = PARAMS.MAX_ITEMS): number {
  return SNAP_HEADER_BYTES + SNAP_STRIDE * creatureCap + ITEM_STRIDE * itemCap;
}

export const SNAPSHOT_POOL_SIZE = 3;

/** Main-thread persistent decode destination (buffers are returned to the worker). */
export class DecodedSnapshot {
  tick = -1;
  time = 0;
  count = 0;
  itemCount = 0;
  arrivalMs = 0;
  dayPhase = 0.3; // 0..1
  readonly x: Float32Array;
  readonly z: Float32Array;
  readonly heading: Float32Array; // radians
  readonly anim: Uint8Array;
  readonly emotion: Uint8Array;
  readonly scaleQ: Uint8Array;
  readonly seed: Uint8Array;
  readonly phase: Uint8Array;
  readonly life: Uint8Array;
  readonly hue: Uint8Array;

  readonly itemId: Uint16Array;
  readonly itemType: Uint8Array;
  readonly itemState: Uint8Array;
  readonly itemX: Float32Array;
  readonly itemZ: Float32Array;
  readonly itemY: Float32Array;

  constructor(capacity: number = PARAMS.INSTANCE_CAP, itemCap: number = PARAMS.MAX_ITEMS) {
    this.x = new Float32Array(capacity);
    this.z = new Float32Array(capacity);
    this.heading = new Float32Array(capacity);
    this.anim = new Uint8Array(capacity);
    this.emotion = new Uint8Array(capacity);
    this.scaleQ = new Uint8Array(capacity);
    this.seed = new Uint8Array(capacity);
    this.phase = new Uint8Array(capacity);
    this.life = new Uint8Array(capacity);
    this.hue = new Uint8Array(capacity);
    this.itemId = new Uint16Array(itemCap);
    this.itemType = new Uint8Array(itemCap);
    this.itemState = new Uint8Array(itemCap);
    this.itemX = new Float32Array(itemCap);
    this.itemZ = new Float32Array(itemCap);
    this.itemY = new Float32Array(itemCap);
  }
}

const TWO_PI = Math.PI * 2;

export function decodeSnapshotInto(
  dst: DecodedSnapshot,
  buf: ArrayBuffer,
  arrivalMs: number,
): void {
  const dv = new DataView(buf);
  dst.tick = dv.getUint32(0, true);
  dst.time = dv.getFloat32(4, true);
  dst.count = Math.min(dv.getUint16(8, true), dst.x.length);
  dst.itemCount = Math.min(dv.getUint16(10, true), dst.itemId.length);
  dst.dayPhase = dv.getUint16(12, true) / 65535;
  dst.arrivalMs = arrivalMs;
  let o = SNAP_HEADER_BYTES;
  for (let i = 0; i < dst.count; i++) {
    dst.x[i] = dv.getInt16(o, true) / POS_SCALE;
    dst.z[i] = dv.getInt16(o + 2, true) / POS_SCALE;
    dst.heading[i] = (dv.getUint8(o + 4) / 256) * TWO_PI;
    dst.anim[i] = dv.getUint8(o + 5);
    dst.emotion[i] = dv.getUint8(o + 6);
    dst.scaleQ[i] = dv.getUint8(o + 7);
    dst.seed[i] = dv.getUint8(o + 8);
    dst.phase[i] = dv.getUint8(o + 9);
    dst.life[i] = dv.getUint8(o + 10);
    dst.hue[i] = dv.getUint8(o + 11);
    o += SNAP_STRIDE;
  }
  for (let i = 0; i < dst.itemCount; i++) {
    dst.itemId[i] = dv.getUint16(o, true);
    dst.itemType[i] = dv.getUint8(o + 2);
    dst.itemState[i] = dv.getUint8(o + 3);
    dst.itemX[i] = dv.getInt16(o + 4, true) / POS_SCALE;
    dst.itemZ[i] = dv.getInt16(o + 6, true) / POS_SCALE;
    dst.itemY[i] = dv.getInt16(o + 8, true) / POS_SCALE;
    o += ITEM_STRIDE;
  }
}
